// DEPLOY: /var/www/woody.wtf/frontend/src/pages/TradingOrders.js
// Trading > Orders (/trading/orders). Everything about orders in one place: open orders
// (Modify / Cancel, and Cancel all - IBKR's global cancel), order history, today's fills
// with commissions and realised P&L, and the site's own order log (every order woody.wtf
// has sent, including rejected ones and currency conversions). Search by code or name,
// filter by side and status, and export any tab to CSV for a trade journal.
// IBKR's API only returns recent orders and today's fills; the site order log keeps the
// full history of orders sent from here, and IBKR statements hold the rest.
// Admin-only for now (useTradingAccount guard; API is @admin_required in trading.py).
import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import TradingTabs from '../components/TradingTabs';
import OpenOrders from '../components/OpenOrders';
import api from '../api';
import {
  s, REFRESH_MS, isNum, num, signed, tone, errText,
  Table, DataTable, Tile, useTradingAccount, StatusBar, useAccountData, ORDER_COLS, FILL_COLS, downloadCsv,
} from '../components/tradingShared';

const LOG_LIMIT = 1000;
const SIDES = [['', 'All sides'], ['BUY', 'Buy'], ['SELL', 'Sell']];
const sideOf = r => r.side === 'BOT' ? 'BUY' : r.side === 'SLD' ? 'SELL' : r.side;          // fills use BOT / SLD
const statusOf = r => r.order_status || r.status || '';

export default function TradingOrders() {
  const t = useTradingAccount();
  const { data, err, updated, reload } = useAccountData(t.broker, t.acc, 'AUD');
  const [tab, setTab]       = useState('open');
  const [q, setQ]           = useState('');
  const [side, setSide]     = useState('');
  const [status, setStatus] = useState('');
  const [log, setLog]       = useState(null);
  const [logErr, setLogErr] = useState('');
  const [tick, setTick]     = useState(0);
  const [msg, setMsg]       = useState('');
  const [actErr, setActErr] = useState('');

  useEffect(() => { document.title = 'Orders | chart.ninja'; }, []);
  useEffect(() => {
    if (!t.broker) return;
    api.get(`/trading/${t.broker}/order-log`, { params: { limit: LOG_LIMIT } })
      .then(r => { setLog(r.data.orders); setLogErr(''); }).catch(e => setLogErr(errText(e)));
  }, [t.broker, tick]);

  const changed = () => { reload(); setTick(x => x + 1); };
  const cancelAll = async () => {
    const n = data?.orders?.length || 0;
    if (!window.confirm(`Cancel ALL ${n} open order${n === 1 ? '' : 's'}?\n\nThis is IBKR's global cancel - it also cancels orders placed from the IBKR app or website.`)) return;
    setMsg(''); setActErr('');
    try { setMsg((await api.post(`/trading/${t.broker}/orders/cancel-all`, { acc_id: t.acc.acc_id })).data.message); changed(); }
    catch (e) { setActErr(errText(e)); }
  };

  // Search / side / status filters, applied to whichever tab is showing.
  const match = r => {
    const text = `${r.code || ''} ${r.desc || ''} ${r.order_id || ''} ${r.broker_order_id || ''}`.toLowerCase();
    return (!q || text.includes(q.toLowerCase())) && (!side || sideOf(r) === side) && (!status || statusOf(r) === status);
  };
  const lists = {
    open:    data?.orders || [],
    history: data?.order_history || [],
    fills:   data?.deals || [],
    log:     log || [],
  };
  const shown = (lists[tab] || []).filter(match);
  const statuses = [...new Set(lists[tab].map(statusOf).filter(Boolean))].sort();

  const fills = data?.deals || [];
  const commission = fills.reduce((a, f) => a + (Number(f.commission) || 0), 0);
  const realised = fills.reduce((a, f) => a + (isNum(f.realized_pnl) ? Number(f.realized_pnl) : 0), 0);
  const tabs = [['open', 'Open orders'], ['history', 'Order history'], ['fills', "Today's fills"], ['log', 'Site order log']];

  return (
    <div style={s.page}>
      <Navbar />
      <div style={s.content}>
        <h1 style={s.heading}>Trading</h1>
        <TradingTabs />
        <StatusBar t={t} />
        {t.acc && (
          <>
            <div style={s.tiles}>
              <Tile label="Open orders" value={data ? lists.open.length : '—'} />
              <Tile label="Fills today" value={data ? fills.length : '—'} />
              <Tile label="Commission today" value={data ? num(commission) : '—'} sub="per fill currency" />
              <Tile label="Realised today" value={data ? signed(realised) : '—'} color={tone(realised)} sub="from today's fills" />
              <Tile label="Orders sent from site" value={log ? log.length : '—'} sub={log?.length === LOG_LIMIT ? `latest ${LOG_LIMIT}` : 'all time'} />
            </div>

            <div style={s.card}>
              <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', flexWrap: 'wrap', alignItems: 'center' }}>
                {tabs.map(([k, label]) => (
                  <button key={k} style={s.tab(tab === k)} onClick={() => { setTab(k); setStatus(''); }}>
                    {label}<span style={s.count}>{lists[k].length}</span>
                  </button>
                ))}
                <span style={{ ...s.meta, marginLeft: 'auto' }}>
                  {updated ? `Updated ${updated.toLocaleTimeString('en-GB')} · refreshes every ${REFRESH_MS / 1000}s` : 'Loading…'}</span>
              </div>

              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', margin: '14px 0 6px', flexWrap: 'wrap' }}>
                <input style={{ ...s.input, width: '240px' }} value={q} onChange={e => setQ(e.target.value)} placeholder="Search code, name or order ID" />
                <select style={s.select} value={side} onChange={e => setSide(e.target.value)}>
                  {SIDES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                {tab !== 'fills' && (
                  <select style={s.select} value={status} onChange={e => setStatus(e.target.value)}>
                    <option value="">All statuses</option>
                    {statuses.map(x => <option key={x}>{x}</option>)}
                  </select>
                )}
                <span style={s.meta}>{shown.length} shown</span>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                  {tab === 'open' && lists.open.length > 0 && (
                    <button style={{ ...s.btn, color: 'var(--error)', borderColor: 'var(--error)' }} onClick={cancelAll}>Cancel all</button>
                  )}
                  <button style={s.btn} disabled={!shown.length}
                          onClick={() => downloadCsv(`woody-${tab}-${new Date().toISOString().slice(0, 10)}.csv`, shown)}>Export CSV</button>
                </div>
              </div>
              {msg && <div style={s.ok}>{msg}</div>}
              {(actErr || err) && <div style={s.err}>✕ {actErr || err}</div>}

              {!data && !err && tab !== 'log' && <div style={{ ...s.meta, padding: '16px 0' }}>Loading…</div>}
              {data && tab === 'open' && <OpenOrders broker={t.broker} acc={t.acc} orders={shown} onChange={changed} />}
              {data && tab === 'history' && <Table cols={ORDER_COLS} rows={shown} empty="No orders match." />}
              {data && tab === 'fills' && (
                <>
                  <Table cols={FILL_COLS} rows={shown} empty="No fills today." />
                  <div style={{ ...s.meta, marginTop: '8px' }}>IBKR's API reports today's fills. Earlier fills are in your IBKR statements.</div>
                </>
              )}
              {tab === 'log' && (
                <>
                  {logErr && <div style={s.err}>✕ {logErr}</div>}
                  {log && <DataTable rows={shown} empty="No orders match." />}
                  <div style={{ ...s.meta, marginTop: '8px' }}>Every order chart.ninja has sent, including rejected ones and currency conversions.</div>
                </>
              )}
            </div>
            {tab === 'history' && <div style={s.meta}>Order history covers the recent orders IBKR still reports; older ones are in your IBKR statements.</div>}
          </>
        )}
      </div>
    </div>
  );
}
